/* eslint-disable no-console */
import { SanityClient } from "@sanity/client";
import { SearchIndex } from "algoliasearch";
import { DereferencedVariant } from "../../sanity/variant-queries";
import { AlgoliaFacets, AlgoliaTags, SourceFacet } from "../types";

/**
 * Extract records from Sanity, optionally transform, and then push to Algolia
 *
 * Runs the provided query against Sanity and then indexes the records in Algolia.
 * If a transformation callback is provided, the records will be transformed prior to
 * indexing.
 *
 * @template SanityRecordT Sanity record type
 * @template AlgoliaRecordT Record type to be indexed; may be same as SanityRecordT if there is no transformation step
 *
 * @param {SanityClient} sanityClient Sanity client
 * @param {string} sanityQuery Sanity query string
 * @param {AlgoliaIndex} searchIndex Algolia search index to which transformed records should be pushed
 * @param {(records: SanityRecordT | SanityRecordT[]) => AlgoliaRecordT[]} transform Optional callback for transforming Sanity records before pushing to Algolia
 */
export function etl<SanityRecordT, AlgoliaRecordT>(
  sanityClient: SanityClient,
  sanityQuery: string,
  searchIndex: SearchIndex,
  transform: (record: SanityRecordT | SanityRecordT[]) => AlgoliaRecordT[],
) {
  return sanityClient
    .fetch<SanityRecordT | SanityRecordT[]>(sanityQuery)
    .then(transform)
    .then(searchIndex.replaceAllObjects)
    .then((r) =>
      console.log(
        `Replaced ${searchIndex.indexName} with ${r.objectIDs.length} objects:`,
        r,
      ),
    )
    .catch((e) => {
      console.log(e);
      throw Error(JSON.stringify(e));
    });
}

/**
 * Prepares Sanity DereferencedVariant data to be attached to an Algolia Product
 *
 * @param {DereferencedVariant[]} variants Records to transform
 *
 * @returns {DereferencedVariant[]} Transformed records
 */
export function transformVariants(
  variants: DereferencedVariant[],
): DereferencedVariant[] {
  return variants.map(
    ({
      _id,
      _rev,
      _type,
      _createdAt,
      _updatedAt,
      label,
      price,
      inventoryLimited,
      initialInventoryCount,
      isWaitListable,
      cartLimit,
      images,
      forSale,
      assortment,
      deliveryMethods,
      optionValues,
      regularPrice,
      sellingPlans,
      sku,
      netsuiteInternalId,
      netsuiteInternalIdSb,
      gtin,
      totalPackageWeight,
      weight,
    }) => ({
      _id,
      _rev,
      _type,
      _createdAt,
      _updatedAt,
      label,
      price,
      inventoryLimited,
      initialInventoryCount,
      isWaitListable,
      cartLimit,
      images,
      forSale,
      assortment,
      deliveryMethods,
      optionValues,
      regularPrice,
      sellingPlans,
      sku,
      netsuiteInternalId,
      netsuiteInternalIdSb,
      gtin,
      totalPackageWeight,
      weight,
    }),
  );
}

/**
 * Reduces an array of items into a `Record<string, string[]>` by extracting
 * a key and a value from each item. Items where either the key or value is
 * falsy are skipped.
 */
function reduceToMultiValueMap<T>(
  items: T[],
  getKey: (item: T) => string | undefined,
  getValue: (item: T) => string | undefined,
): Record<string, string[]> {
  if (!items?.length) return {};

  return items.reduce(
    (result, item) => {
      const key = getKey(item);
      const value = getValue(item);

      if (!key || !value) return result;

      if (result[key]) {
        result[key].push(value);
      } else {
        Object.assign(result, { [key]: [value] });
      }

      return result;
    },
    {} as Record<string, string[]>,
  );
}

/**
 * Reduces an array of `Tag`s to an object in which the properties are tag prefixes and
 * the values are an array of suffixes. Effectively ignores tags that do not conform to
 * Blue Bottle's `"prefix":"suffix"` convention.
 *
 * @example
 * ```ts
 * const tags = ["process:washed", "process:natural", "roast-level:dark"];
 * const obj = reduceTags(tags);
 *
 * console.log(obj);
 * // { process: ["washed", "natural"], "roast-level": ["dark"] }
 * ```
 */
export function reduceTags(tags: string[]): AlgoliaTags {
  return reduceToMultiValueMap<string>(
    tags,
    (tag) => tag?.split(":")?.[0],
    (tag) => tag?.split(":")?.[1],
  );
}

/**
 * Reduces an array of dereferenced `SourceFacet`s to an object in which the
 * properties are facet category slugs and the values are arrays of facet value slugs.
 *
 * @example
 * ```ts
 * const facets = [
 *   { categorySlug: "roast", valueSlug: "light" },
 *   { categorySlug: "roast", valueSlug: "medium" },
 *   { categorySlug: "origin", valueSlug: "ethiopia" },
 * ];
 * const obj = reduceFacets(facets);
 *
 * console.log(obj);
 * // { roast: ["light", "medium"], origin: ["ethiopia"] }
 * ```
 */
export function reduceFacets(facets: SourceFacet[]): AlgoliaFacets {
  return reduceToMultiValueMap<SourceFacet>(
    facets,
    ({ categorySlug }) => categorySlug,
    ({ valueSlug }) => valueSlug,
  );
}

export function findBestValueVariant(
  productName: string,
  variants: DereferencedVariant[],
): DereferencedVariant {
  if (!variants) {
    console.error(`No variants were present on product: ${productName}`);
  }

  if (variants.length === 1) return variants[0];

  return variants.reduce(
    (prev, current) =>
      prev.regularPrice < current.regularPrice ? prev : current,
    variants[0],
  );
}
